/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  CompanyContainer,
  HeaderTitle,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
} from '../../../services/settings/appStyle/global.style';
import { DropdownButton, Loader, Table } from '../../../Components/uielements';
import TimesheetService from '../../../services/bioplus-services/timesheet';
import { IntlMessages } from '../../../helper/langHelper';
import moment from 'moment';
import { convertToUserTimeZone } from '../../../helper/dataFormat';
import { useCompany } from '../../../hooks/CompanyContext';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import { useUserContext } from '../../../hooks/UserContext';

function extractData(dataArray, userTimeZone) {
  if (dataArray) {
    const extractedData = dataArray?.map((item) => {
      return {
        name: `${item?.userId?.firstName} ${item?.userId?.lastName}`,
        geofenceName: item?.geofenceId?.name,
        date: moment(item?.recordDate).format('DD-MM-YYYY'),
        checkIn: item?.checkin ? convertToUserTimeZone(item?.checkin, userTimeZone) : '--',
        checkOut: item?.checkout ? convertToUserTimeZone(item?.checkout, userTimeZone) : '--',
        createdAt: moment(item?.recordDate).format('DD-MM-YYYY'),
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const AdminAttendance = () => {
  const companyContext = useCompany();

  const companyFilter = companyContext?.company?._id;

  const userContext = useUserContext();
  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find(
    (permission) => permission?.label === 'timesheet'
  );

  const canDownloadReport = !!isUserAllowToManage?.isReport;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const query = useQuery(['timesheetEntriesAdmin', page, pageSize, companyFilter], () =>
    TimesheetService.listTimesheetForAdmin(page, pageSize, companyFilter)
  );

  const adminTimesheetsForReport = useQuery(['timesheetEntriesAdminReport', companyFilter], () =>
    TimesheetService.listTimesheetForAdminReport(companyFilter)
  );

  const userTableData = query?.data?.data ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  //   table coulmns
  const userColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName ?? '--'} ${
              cellData?.row?.original?.userId?.lastName ?? '--'
            }`}
          </TableCell>
        ),
        width: '20%',
      },
      {
        Header: <IntlMessages id="table.col.geofence.name" />,
        accessor: 'geofence',
        Cell: (cellData) => (
          <TableCell>{`${cellData?.row?.original?.geofenceId?.name ?? '--'}`}</TableCell>
        ),
        width: '20%',
      },
      {
        Header: <IntlMessages id="table.col.date" />,
        accessor: 'recordDate',
        width: '16%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.checkIn" />,
        accessor: 'checkin',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? convertToUserTimeZone(cellData.value, userTimeZone) : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.checkOut" />,
        accessor: 'checkout',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? convertToUserTimeZone(cellData.value, userTimeZone) : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const excelFormatData = extractData(adminTimesheetsForReport?.data, userTimeZone);
  const fileName = `Timesheets-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Timesheets-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'name', width: 30 },
    { header: 'Geofence Name', key: 'geofenceName', width: 30 },
    { header: 'Date', key: 'date', width: 30 },
    { header: 'Check In', key: 'checkIn', width: 30 },
    { header: 'Check Out', key: 'checkOut', width: 30 },
    { header: 'created At', key: 'createdAt', width: 30 },
  ];

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  if (query.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.records.attendance" />
        </HeaderTitle>
        {userTableData && userTableData.length > 0 && canDownloadReport && (
          <DropdownButton
            noOfColoumns={1}
            buttonArray={[
              { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
              { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
            ]}
            onClick={handleClickItem}
          />
        )}
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={userTableData}
          columns={userColumns}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={query?.data?.total || 0}
        />
      </TableWrapper>
    </StyledContainer>
  );
};

export default AdminAttendance;
